<template>
  <div class="c_tabs_v2" :data-theme="tabTheme">
    <h2 v-if="data.sectionTitle" class="c_2022--h2_graphik">{{ data.sectionTitle }}</h2>
    <!-- Tabs on desktop -->
    <div v-if="!['sm'].includes($mq)" class="c_wrapper_2022">
      <ul class="c_tabs_v2__titles" role="tablist">
        <li
          class="c_tab"
          v-for="(tab, i) in tabs"
          :key="i"
          :class="{ 'c_tab--active': i === activeIndex }"
          role="presentation"
        >
          <button
            @click="setActive(i)"
            :aria-label="`${$t('Open this panel')} - ${tab?.title}`"
            :id="`tab-${i}`"
            role="tab"
            :aria-controls="`panel-${i}`"
          >
            {{ tab.title }}
          </button>
        </li>
      </ul>
      <div class="c_tab__content_container">
        <transition-group name="accordion__animation-" mode="out-in" tag="div">
          <div
            class="c_tab__content"
            v-for="(tab, i) in tabs"
            v-show="i === activeIndex"
            :key="i + 1"
            role="tabpanel"
            :id="`panel-${i}`"
            :aria-labelledby="`tab-${i}`"
          >
            <TabContent :tab="tab" ref="tabContent" />
          </div>
        </transition-group>
      </div>
    </div>
    <!-- Accordions on mobile -->
    <div v-if="['sm'].includes($mq)" class="c_wrapper_2022">
      <accordion>
        <accordion-item
          v-for="(tab, i) in tabs"
          :key="i"
          :item-index="i"
          :open-behavior="data.mobileTabOpenBehavior"
          :theme="tabTheme"
        >
          <accordion-title slot="title">{{ tab.title }}</accordion-title>
          <accordion-content slot="content">
            <div class="c_tab__content c_tab__content--active">
              <TabContent :tab="tab" ref="tabContent" />
            </div>
          </accordion-content>
        </accordion-item>
      </accordion>
    </div>
  </div>
</template>
<script>
import {
  defineComponent,
  onBeforeMount,
  nextTick,
  getCurrentInstance,
  toRefs,
  reactive,
} from '@nuxtjs/composition-api';
import TabContent from 'Components/tabsv2/TabContent.vue';
import Accordion from 'Components/atom_accordion/Accordion.vue';
import AccordionContent from 'Components/atom_accordion/AccordionContent.vue';
import AccordionItem from 'Components/atom_accordion/AccordionItem.vue';
import AccordionTitle from 'Components/atom_accordion/AccordionTitle.vue';

export default defineComponent({
  name: 'TabsV2',
  props: {
    data: Object,
  },
  components: {
    TabContent,
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTitle,
  },
  setup(props) {
    const state = reactive({
      tabTheme: 'dark',
      themes: { darkMode: 'dark', lightMode: 'light' },
      themeKey: props?.data?.tabTheme ?? 'dark',
      tabs: null,
      activeIndex: 0,
    });

    const { proxy } = getCurrentInstance();

    // Set theme based on prop, default to dark mode.
    state.tabTheme = state.themes?.[state.themeKey] ?? 'dark';

    const mediaKind = {
      images_Asset: 'image',
      videos_Asset: 'video',
      videoUploads_Asset: 'videoUpload',
    };

    const onToggle = (i) => {
      // Disregard clicks on current tab
      // Update reactive value to clicked index
      state.activeIndex = state.activeIndex === i ? null : i;
    };

    const pauseAllVids = () => {
      document.querySelectorAll('video').forEach((v) => v.pause?.());
    };

    const resetAllTabs = () => {
      const tabRefs = proxy.$refs.tabContent;
      tabRefs.forEach((tabRef) => tabRef?.resetTab?.());
    };

    const setActive = (i) => {
      pauseAllVids();
      resetAllTabs();
      // Set new active tab
      state.activeIndex = i;
    };

    onBeforeMount(() => {
      nextTick(() => {
        // Map the prop into more readable and iterable object for tab before mounting
        state.tabs = (props?.data?.tabsV2LandingPage ?? []).map((tab) => {
          // Craft asset GraphQL
          const media = tab.tabMedia?.[0];
          // Craft asset volume handle
          const type = media?.__typename;
          return {
            mediaAlt: media?.title || media?.videoThumbnail?.[0]?.title || '',
            media: media?.galleryEnlarged || media?.videoThumbnail?.[0]?.galleryEnlarged || null,
            videoUrl: media?.url ?? null,
            mediaType: mediaKind[type],
            mediaObjectFit: tab.mediaObjectFit ?? 'cover',
            videoId: media?.videoId ?? null,
            title: tab.tabTitle ?? '',
            contentTitle: tab.contentTitle ?? '',
            body: tab.contentBody ?? '',
            cta: tab.contentCta ?? [],
          };
        });
      });
    });

    return { ...toRefs(state), setActive, onToggle };
  },
});
</script>

<style lang="scss">
.c_tabs_v2 {
  position: relative;
  background-color: var(--tab-bg-color);
  color: var(--tab-text-color);
  padding: px_to_rem(40) 0;
  overflow-y: hidden;

  @include mq($mq_sm_to_md) {
    padding: px_to_rem(80) 0;
  }
  h2 {
    @include font('graphik_semibold');
    text-align: center;
    margin-bottom: px_to_rem(72);
  }
  h3 {
    @include font('graphik_semibold');
    margin-bottom: px_to_rem(16);
  }
  a {
    color: var(--tab-link-color);
    @include font('graphik_semibold');
    align-self: flex-start;
    &:hover {
      text-decoration: none;
    }
  }
  button {
    color: var(--tab-button-color);
  }
  &[data-theme='light'] {
    --tab-bg-color: var(--color-white);
    --tab-text-color: var(--color-black);
    --tab-link-color: var(--color-red);
    --tab-button-color: var(--color-black);
    --tab-button-hover: rgba(0, 0, 0, 0.03);
  }
  &[data-theme='dark'] {
    --tab-bg-color: var(--color-black);
    --tab-text-color: var(--color-white);
    --tab-link-color: var(--color-yellow);
    --tab-button-color: var(--color-white);
    --tab-button-hover: rgba(255, 255, 255, 0.08);
  }
  .c_tab__content {
    padding: px_to_rem(20) 0;

    @include mq($mq_sm_to_md) {
      padding: px_to_rem(80) 0;
    }

    &--flex {
      width: 100%;
      display: flex;
      gap: px_to_rem(20);
      flex-direction: column;
      @include mq($mq_md_to_lg) {
        gap: px_to_rem(48);
      }
      @include mq($mq_sm_to_md) {
        flex-direction: row;
        justify-content: center;
      }
    }
  }
  .c_tab__content_wrapper {
    & > div {
      display: flex;
    }
  }
  .c_tabs_v2__titles {
    display: flex;
    justify-content: center;
    align-items: center;
    list-style-type: none;
    gap: px_to_rem(20);
    li {
      flex-grow: 1;
      flex-basis: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    button {
      width: 100%;
      height: 100%;
      display: block;
      background-color: var(--tab-bg-color);
      padding: px_to_rem(20);
      border: none;
      @include font('graphik_semibold');
      font-size: px_to_rem(18);
      line-height: px_to_rem(28);
      text-align: center;
      transition: background-color cubic-bezier(0.4, 0, 0.6, 1) 0.15s;
      &:hover {
        background-color: var(--tab-button-hover);
      }
    }
  }
  .c_tab {
    border-bottom: 2px solid transparent;
    &--active {
      border-bottom: 2px solid var(--tab-button-color);
    }
  }
  .c_tab__content_container {
    position: relative;
  }
  .accordion__animation--enter-active,
  .accordion__animation--leave-active {
    transition: opacity 150ms cubic-bezier(1, 0.5, 0.8, 1);
    will-change: opacity;
  }
  .accordion__animation--enter,
  .accordion__animation--leave-to {
    opacity: 0;
  }
  .accordion__animation--move {
    transition: opacity 150ms cubic-bezier(1, 0.5, 0.8, 1);
    will-change: opacity;
  }
  .accordion__animation--leave-active {
    position: absolute;
    left: 0;
    top: 0;
    pointer-events: none;
    z-index: 1;
  }
}
</style>
